import "@/index.css";
import { useEffect, lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { trackPageView } from "@/lib/analytics";
import { loadHotjar, hotjarStateChange } from "@/lib/hotjar";
import { installImageOptimizer } from "@/lib/autoImage";
import { StoreProvider } from "@/context/StoreContext";
import { AuthProvider } from "@/context/AuthContext";
import Layout from "@/components/Layout";
import Home from "@/pages/Home";

// Route-level code splitting — each page loads only when visited.
// lazyRetry auto-reloads once if a chunk fetch races a dev-server recompile/deploy.
const lazyRetry = (factory) =>
  lazy(async () => {
    try {
      return await factory();
    } catch (e) {
      if (!sessionStorage.getItem("lazy-retry")) {
        sessionStorage.setItem("lazy-retry", "1");
        window.location.reload();
        return new Promise(() => {});
      }
      sessionStorage.removeItem("lazy-retry");
      throw e;
    }
  });

const Collection = lazyRetry(() => import("@/pages/Collection"));
const ProductDetail = lazyRetry(() => import("@/pages/ProductDetail"));
const CartPage = lazyRetry(() => import("@/pages/CartPage"));
const Wishlist = lazyRetry(() => import("@/pages/Wishlist"));
const SearchResults = lazyRetry(() => import("@/pages/SearchResults"));
const About = lazyRetry(() => import("@/pages/About"));
const PolicyPage = lazyRetry(() => import("@/pages/PolicyPage"));
const CustomPage = lazyRetry(() => import("@/pages/CustomPage"));
const Account = lazyRetry(() => import("@/pages/Account"));
const AdminPromos = lazyRetry(() => import("@/pages/AdminPromos"));
const AdminOrders = lazyRetry(() => import("@/pages/AdminOrders"));
const AdminProducts = lazyRetry(() => import("@/pages/AdminProducts"));
const AdminCategories = lazyRetry(() => import("@/pages/AdminCategories"));
const AdminProductTabs = lazyRetry(() => import("@/pages/AdminProductTabs"));
const AdminPolicies = lazyRetry(() => import("@/pages/AdminPolicies"));
const AdminWhatsApp = lazyRetry(() => import("@/pages/AdminWhatsApp"));
const AdminWhatsAppInbox = lazyRetry(() => import("@/pages/AdminWhatsAppInbox"));
const AdminHomepage = lazyRetry(() => import("@/pages/AdminHomepage"));
const AdminNav = lazyRetry(() => import("@/pages/AdminNav"));
const AdminPages = lazyRetry(() => import("@/pages/AdminPages"));
const WhatsAppOptOut = lazyRetry(() => import("@/pages/WhatsAppOptOut"));
const AdminCustomers = lazyRetry(() => import("@/pages/AdminCustomers"));
const AdminManifest = lazyRetry(() => import("@/pages/AdminManifest"));
const Login = lazyRetry(() => import("@/pages/Login"));
const ForgotPassword = lazyRetry(() => import("@/pages/ForgotPassword"));
const ResetPassword = lazyRetry(() => import("@/pages/ResetPassword"));
const FAQs = lazyRetry(() => import("@/pages/FAQs"));
const Care = lazyRetry(() => import("@/pages/Care"));
const Contact = lazyRetry(() => import("@/pages/Contact"));
const AdminMessages = lazyRetry(() => import("@/pages/AdminMessages"));
const AdminUsers = lazyRetry(() => import("@/pages/AdminUsers"));
const AdminReviews = lazyRetry(() => import("@/pages/AdminReviews"));
const AdminAnalytics = lazyRetry(() => import("@/pages/AdminAnalytics"));
const Checkout = lazyRetry(() => import("@/pages/Checkout"));
const OrderConfirmation = lazyRetry(() => import("@/pages/OrderConfirmation"));
const AuthCallback = lazyRetry(() => import("@/pages/AuthCallback"));

function RouteFallback() {
  return (
    <div className="min-h-[60vh] flex items-center justify-center" data-testid="route-loading">
      <div className="w-6 h-6 border-2 border-lemce-beige border-t-lemce-matte rounded-full animate-spin" />
    </div>
  );
}

function AppRouter() {
  const location = useLocation();
  useEffect(() => { try { sessionStorage.removeItem("lazy-retry"); } catch (e) {} }, []);
  useEffect(() => { loadHotjar(); }, []);
  useEffect(() => { const cleanup = installImageOptimizer(); return cleanup; }, []);
  useEffect(() => {
    trackPageView(`${location.pathname}${location.search}`);
    hotjarStateChange(`${location.pathname}${location.search}${location.hash}`);
  }, [location.pathname, location.search, location.hash]);
  useEffect(() => {
    if (!location.hash) window.scrollTo(0, 0);
  }, [location.pathname]);
  // Handle Emergent Google OAuth return (session_id in URL fragment) FIRST
  if (location.hash?.includes("session_id=")) {
    return <Suspense fallback={<RouteFallback />}><AuthCallback /></Suspense>;
  }
  return (
    <Suspense fallback={<RouteFallback />}>
      <Routes>
        <Route element={<Layout />}>
          <Route path="/" element={<Home />} />
          <Route path="/collection/:slug" element={<Collection />} />
          <Route path="/product/:slug" element={<ProductDetail />} />
          <Route path="/cart" element={<CartPage />} />
          <Route path="/checkout" element={<Checkout />} />
          <Route path="/order/:orderId" element={<OrderConfirmation />} />
          <Route path="/wishlist" element={<Wishlist />} />
          <Route path="/search" element={<SearchResults />} />
          <Route path="/about" element={<About />} />
          <Route path="/faqs" element={<FAQs />} />
          <Route path="/care" element={<Care />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/shipping" element={<PolicyPage pageKey="shipping" />} />
          <Route path="/returns" element={<PolicyPage pageKey="returns" />} />
          <Route path="/p/:slug" element={<CustomPage />} />
          <Route path="/account" element={<Account />} />
          <Route path="/admin/promos" element={<AdminPromos />} />
          <Route path="/admin/orders" element={<AdminOrders />} />
          <Route path="/admin/products" element={<AdminProducts />} />
          <Route path="/admin/categories" element={<AdminCategories />} />
          <Route path="/admin/product-tabs" element={<AdminProductTabs />} />
          <Route path="/admin/policies" element={<AdminPolicies />} />
          <Route path="/admin/whatsapp" element={<AdminWhatsApp />} />
          <Route path="/admin/whatsapp/inbox" element={<AdminWhatsAppInbox />} />
          <Route path="/admin/homepage" element={<AdminHomepage />} />
          <Route path="/admin/nav" element={<AdminNav />} />
          <Route path="/admin/pages" element={<AdminPages />} />
          <Route path="/whatsapp/opt-out" element={<WhatsAppOptOut />} />
          <Route path="/admin/customers" element={<AdminCustomers />} />
          <Route path="/admin/messages" element={<AdminMessages />} />
          <Route path="/admin/admins" element={<AdminUsers />} />
          <Route path="/admin/reviews" element={<AdminReviews />} />
          <Route path="/admin/analytics" element={<AdminAnalytics />} />
          <Route path="/login" element={<Login />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
        </Route>
        <Route path="/admin/manifest" element={<AdminManifest />} />
      </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <StoreProvider>
            <AppRouter />
          </StoreProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
